import { AuthorTypeEnum } from '@/entities/authors'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type SorterResult } from 'antd/es/table/interface'
import { combine, createEvent, createStore, sample } from 'effector'
import { debounce } from 'patronum'

import {
  type GetStatisticsAuthorsQueryFinishedSuccessResult,
  type GetStatisticsAuthorsQueryFinishedSuccessResultAuthorsItem,
  type GetStatisticsAuthorsQueryStart,
  getStatisticsAuthorsQuery,
} from '../api'
import {
  parseAuthorsStatisticsSortByToSorterResult,
  parseAuthorsStatisticsSorterResultToSortBy,
} from '../lib/types/authors'
import {
  $rangeAsParams,
  StatisticsPageGate,
  rangeDebounced,
} from './statistics-range.model'

// Author Type

export const authorTypeChanged =
  createEvent<GetStatisticsAuthorsQueryStart['params']['AuthorType']>()
export const $authorType = createStore<
  GetStatisticsAuthorsQueryStart['params']['AuthorType']
>(AuthorTypeEnum.Artist).on(authorTypeChanged, (_, payload) => payload)

// Sort By
export const sortByReset = createEvent()
export const sortByChanged =
  createEvent<GetStatisticsAuthorsQueryStart['params']['SortBy']>()
export const sortByChangedBySorterResult =
  createEvent<
    SorterResult<GetStatisticsAuthorsQueryFinishedSuccessResultAuthorsItem>
  >()
export const $sortBy = createStore<
  GetStatisticsAuthorsQueryStart['params']['SortBy']
>(undefined, {
  skipVoid: false,
})
  .on(sortByChanged, (_, payload) => payload)
  .on(sortByChangedBySorterResult, (_, payload) =>
    parseAuthorsStatisticsSorterResultToSortBy(payload),
  )
  .reset([sortByReset, StatisticsPageGate.close])

export const $sorterResult = $sortBy.map((sortBy) =>
  parseAuthorsStatisticsSortByToSorterResult(sortBy),
)

// Search Query
export const searchQueryChanged =
  createEvent<GetStatisticsAuthorsQueryStart['params']['SearchQuery']>()
export const $searchQuery = createStore<
  GetStatisticsAuthorsQueryStart['params']['SearchQuery']
>(undefined, {
  skipVoid: false,
}).on(searchQueryChanged, (_, payload) => payload)

// Pagination
export const pageChanged = createEvent<number>()
export const limitChanged = createEvent<number>()
const pageReset = createEvent()
export const $page = createStore<number>(DEFAULT_PAGE)
  .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
  .reset([pageReset, limitChanged, getStatisticsAuthorsQuery.reset])
export const $limit = createStore<number>(DEFAULT_PAGE_SIZE)
$limit.on(limitChanged, (_, size) => size)
const $offset = combine(
  { page: $page, limit: $limit },
  ({ page, limit }) => (page - 1) * limit,
)

sample({
  source: getStatisticsAuthorsQuery.$status,
  clock: [$authorType, $searchQuery, $sortBy],
  filter: (status) => status !== 'initial',
  target: pageReset,
})

// Request
const $request = combine(
  {
    sortBy: $sortBy,
    searchQuery: $searchQuery,
    authorType: $authorType,
    offset: $offset,
    limit: $limit,
  },
  ({
    sortBy,
    searchQuery,
    authorType,
    offset,
    limit,
  }): GetStatisticsAuthorsQueryStart => ({
    params: {
      AuthorType: authorType,
      Limit: limit,
      Offset: offset,
      SortBy: sortBy,
      SearchQuery: searchQuery,
    },
  }),
)

const requestDebounced = debounce({
  source: $request,
  timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
})

export const $isLoadingInitially = combine(
  {
    pending: getStatisticsAuthorsQuery.$pending,
    data: getStatisticsAuthorsQuery.$data,
    error: getStatisticsAuthorsQuery.$error,
    status: getStatisticsAuthorsQuery.$status,
  },
  ({ pending, data, error, status }) =>
    (pending || status !== 'done') && !data && !error,
)

export const $isLoadingMore = combine(
  {
    pending: getStatisticsAuthorsQuery.$pending,
    data: getStatisticsAuthorsQuery.$data,
    error: getStatisticsAuthorsQuery.$error,
    status: getStatisticsAuthorsQuery.$status,
  },
  ({ pending, data, error }) => pending || (!data && !error),
)
export const $tableTotalItems = createStore<number>(0)
  .on(getStatisticsAuthorsQuery.finished.success, (_, payload) => {
    const { authors } = payload.result
    return authors?.totalItems || 0
  })
  .reset(getStatisticsAuthorsQuery.reset)

export const $tableData = createStore<
  NonNullable<
    GetStatisticsAuthorsQueryFinishedSuccessResult['authors']
  >['items']
>([])
  .on(getStatisticsAuthorsQuery.finished.success, (_, payload) => {
    const authors = payload.result.authors
    return authors ? authors.items : []
  })
  .reset(getStatisticsAuthorsQuery.reset)

export const $totalData = createStore<Omit<
  GetStatisticsAuthorsQueryFinishedSuccessResult,
  'authors'
> | null>(null)
  .on(getStatisticsAuthorsQuery.finished.success, (_, payload) => {
    const { authors, ...rest } = payload.result
    return rest
  })
  .reset(getStatisticsAuthorsQuery.reset)

sample({
  source: combine({ request: $request, range: $rangeAsParams }),
  clock: [rangeDebounced, requestDebounced],
  fn: ({ request, range }): GetStatisticsAuthorsQueryStart => ({
    params: {
      ...request.params,
      ...range.params,
    },
  }),
  target: getStatisticsAuthorsQuery.start,
})
sample({
  clock: StatisticsPageGate.close,
  target: getStatisticsAuthorsQuery.reset,
})
